import { Inject, Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  GBMConversationItemModel,
  GBMMessageItemModel,
  GBMMessageListModel,
} from '../models/GBMessage.model';
import { LeadModel, LeadsModel } from '../models/Lead.model';
import { NotificationModel } from '../models/Notification.model';
import { ReviewModel, ReviewsModel } from '../models/Review.model';
import {
  IMessagesRepository,
  MESSAGES_REPOSITORY,
} from '../repositories/IMessages.repository';
import { IMessagesService } from './IMessages.service';

@Injectable({
  providedIn: 'root',
})
export class MessagesService implements IMessagesService {
  constructor(
    @Inject(MESSAGES_REPOSITORY) private repository: IMessagesRepository
  ) {}

  getLeads(): Observable<LeadsModel> {
    return this.repository
      .getLeads()
      .pipe(
        map((response) =>
          LeadsModel.fromApiResponse(response.data.attributes || [])
        )
      );
  }

  setLeadAsRead(id: number): Observable<LeadModel> {
    return this.repository.setLeadAsRead(id).pipe(
      map((response) =>
        LeadModel.fromApiResponse({
          ...response.data.attributes,
          id: +response.data.id,
        })
      )
    );
  }

  getReviews(): Observable<ReviewsModel> {
    return this.repository
      .getReviews()
      .pipe(map((response) => ReviewsModel.fromApiResponse(response.data)));
  }

  getNotifications(): Observable<any> {
    return this.repository.getNotifications().pipe(
      map((response) => {
        if (!response.data.attributes) {
          return [];
        }
        return response.data.attributes;
      })
    );
  }

  setNotificationAsRead(id: number): Observable<any> {
    return this.repository.setNotificationAsRead(id).pipe(
      map((response) =>
       response.data.attributes,
      )
    );
  }

  deleteNotification(id: number): Promise<any> {
    return this.repository
      .deleteNotification(id)
      .pipe(map((response) => response))
      .toPromise();
  }

  getReview(reviewId: string) {
    return this.repository
      .getReview(reviewId)
      .pipe(map((response) => ReviewModel.fromApiResponse(response.data)));
  }

  upsertReply(review: ReviewModel, comment: string) {
    return this.repository.upsertReply(review.id as string, comment).pipe(
      map(
        (response) =>
          new ReviewModel({
            id: review.id,
            senderName: review.senderName,
            message: review.message,
            createTime: review.createTime,
            source: review.source,

            senderPhotoUrl: review.senderPhotoUrl,
            starRating: review.starRating,
            updateTime: review.updateTime,
            reviewReply: {
              comment,
              updateTime: response.data.updateTime,
            },
          })
      )
    );
  }

  deleteReply(reviewId: string) {
    return this.repository.deleteReply(reviewId).pipe(
      map((response) => {
        if (response.status === 'success') {
          return response.status === 'success';
        } else {
          throw Error(response.message || 'Unknown error');
        }
      })
    );
  }

  getReviewLink(): Observable<string> {
    return this.repository
      .getReviewLink()
      .pipe(
        map((response) =>
          response.data.attributes.newReviewUrl || ''
        )
      );
  }

  getGBMessages() {
    return this.getGBMConversationList();
    /*.pipe(
            map(convList => {
                const observableList = [];
                convList.conversations.forEach(conv => {
                    observableList.push(this.getGBMMessageList(conv.conversationId));
                });

                return forkJoin(observableList);
            })*
        );*/
  }

  getGBMConversationList(): Observable<any[]> {
    return this.repository.getGBMConversationList().pipe(
      map((response) => {
        if (!response.data.attributes) {
          return [];
        }
        let arrayData = [];
        Object.keys(response.data.attributes).forEach(key => {
          arrayData.push(
            GBMConversationItemModel.fromApiGMBResponse(response.data.attributes[key])
          );
        });
        return arrayData;
       })
    );
  }

  getGBMMessageList(conversationId: string): Observable<GBMMessageListModel> {
    return this.repository
      .getGBMMessageList(conversationId)
      .pipe(
        map((response) => GBMMessageListModel.fromApiResponse(response.data))
      );
  }

  replyGBMMessage(
    conversationId: string,
    message: string
  ): Observable<any> {
    return this.repository.replyGBMMessage(conversationId, message).pipe(
      map((response) => 
        GBMMessageItemModel.fromApiReplyResponse({
          createTime: moment().format('YYYY-MM-DD hh:mm:ss'),
          id: 0,
          message: message,
          senderName: "",
          source: "GBM_MESSAGES",
          type: "owner"
        })
      )
    );
  }
}
